import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { showErrorThunk, showWarningThunk } from '@spike/notifications-action';
import { useAuth } from 'hooks';
import { fetchIdogcamCameras, fetchIdogcamIntegration, syncIdogcamCameras } from '../api/IdogcamApi';
import { IdogcamCamera, IdogcamLocation } from '../model/IdogcamModel';

const SEARCH_DEBOUNCE_MS = 300;
const DEFAULT_PAGE_SIZE = 10;

export interface IdogcamCamerasState {
    cameras: Array<IdogcamCamera>;
    locations: Array<IdogcamLocation>;
    configured: boolean;
    page: number;
    pageSize: number;
    totalPages: number;
    totalRecords: number;
    searchText: string;
    // First load of a location (integration check + sync + list).
    isLoadingLocation: boolean;
    // Page / size / search changes within the same location.
    isLoadingPage: boolean;
    setSearchText: (text: string) => void;
    setPage: (page: number) => void;
    setPageSize: (size: number) => void;
    reload: () => void;
    // Reloads the list after connecting this location (the connection flow already synced).
    applyConnection: () => void;
    // Call right before switching to a location that was just connected and synced.
    skipNextSync: () => void;
    replaceCamera: (camera: IdogcamCamera) => void;
}

// Loads the iDogCam cameras of one location (marketplace). Each location has its own
// iDogCam account, so switching location re-checks the integration and re-syncs.
export const useIdogcamCameras = (marketplaceId: number): IdogcamCamerasState => {
    const auth = useAuth();
    const dispatch = useDispatch();
    const token = auth.token!;

    const [cameras, setCameras] = useState<Array<IdogcamCamera>>([]);
    const [locations, setLocations] = useState<Array<IdogcamLocation>>([]);
    const [configured, setConfigured] = useState(true);
    const [pagination, setPagination] = useState({ totalPages: 0, totalRecords: 0 });
    const [pageInfo, setPageInfo] = useState({ page: 1, pageSize: DEFAULT_PAGE_SIZE, query: '' });
    const [searchText, setSearchTextState] = useState('');
    const [isLoadingLocation, setIsLoadingLocation] = useState(true);
    const [isLoadingPage, setIsLoadingPage] = useState(false);

    // Ignores responses from requests superseded by a newer one (fast typing, location switch).
    const requestIdRef = useRef(0);
    const isLocationReadyRef = useRef(false);
    const locationRequestIdRef = useRef(0);
    const skipNextSyncRef = useRef(false);

    const loadCameras = useCallback(
        async (page: number, pageSize: number, query: string) => {
            const requestId = ++requestIdRef.current;
            setIsLoadingPage(true);
            try {
                const result = await fetchIdogcamCameras({ marketplaceId, token, page, size: pageSize, query });
                if (requestId !== requestIdRef.current) return;
                setConfigured(result.configured);
                setLocations(result.locations);
                setCameras(result.cameras);
                setPagination({ totalPages: result.totalPages, totalRecords: result.totalRecords });
            } catch {
                if (requestId !== requestIdRef.current) return;
                dispatch(showErrorThunk("We couldn't load the cameras. Please try again."));
            } finally {
                if (requestId === requestIdRef.current) setIsLoadingPage(false);
            }
        },
        [marketplaceId, token, dispatch]
    );

    const loadLocation = useCallback(async () => {
        const locationRequestId = ++locationRequestIdRef.current;
        isLocationReadyRef.current = false;
        const skipSync = skipNextSyncRef.current;
        skipNextSyncRef.current = false;
        setIsLoadingLocation(true);
        setSearchTextState('');
        setPageInfo({ page: 1, pageSize: DEFAULT_PAGE_SIZE, query: '' });

        try {
            const locationIntegration = await fetchIdogcamIntegration(marketplaceId, token);
            if (locationRequestId !== locationRequestIdRef.current) return;
            if (locationIntegration.configured && !skipSync) {
                try {
                    // On-demand sync (MVP): pulls new/renamed cameras from iDogCam before listing.
                    await syncIdogcamCameras(marketplaceId, token);
                } catch {
                    dispatch(
                        showWarningThunk("We couldn't refresh cameras from iDogCam. Showing the last synced list.")
                    );
                }
            }
        } catch {
            // The list request below reports the error; nothing else to do here.
        }

        if (locationRequestId !== locationRequestIdRef.current) return;
        await loadCameras(1, DEFAULT_PAGE_SIZE, '');
        if (locationRequestId !== locationRequestIdRef.current) return;
        isLocationReadyRef.current = true;
        setIsLoadingLocation(false);
    }, [marketplaceId, token, loadCameras, dispatch]);

    useEffect(() => {
        loadLocation();
    }, [loadLocation]);

    useEffect(() => {
        if (!isLocationReadyRef.current) return;
        loadCameras(pageInfo.page, pageInfo.pageSize, pageInfo.query);
    }, [pageInfo]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!isLocationReadyRef.current) return;
        const timeout = setTimeout(() => {
            setPageInfo(prev => (prev.query === searchText ? prev : { ...prev, page: 1, query: searchText }));
        }, SEARCH_DEBOUNCE_MS);
        return () => clearTimeout(timeout);
    }, [searchText]);

    return {
        cameras,
        locations,
        configured,
        page: pageInfo.page,
        pageSize: pageInfo.pageSize,
        totalPages: pagination.totalPages,
        totalRecords: pagination.totalRecords,
        searchText,
        isLoadingLocation,
        isLoadingPage,
        setSearchText: setSearchTextState,
        setPage: page => setPageInfo(prev => ({ ...prev, page })),
        setPageSize: pageSize => setPageInfo(prev => ({ ...prev, page: 1, pageSize })),
        reload: () => loadCameras(pageInfo.page, pageInfo.pageSize, pageInfo.query),
        applyConnection: () => {
            setSearchTextState('');
            // A new pageInfo object always triggers the list effect, which loads page 1.
            isLocationReadyRef.current = true;
            setPageInfo({ page: 1, pageSize: DEFAULT_PAGE_SIZE, query: '' });
        },
        skipNextSync: () => {
            skipNextSyncRef.current = true;
        },
        // Updates a saved camera in place, keeping the current page, search and scroll.
        replaceCamera: (camera: IdogcamCamera) =>
            setCameras(prev => prev.map(current => (current.id === camera.id ? camera : current)))
    };
};

export default useIdogcamCameras;
